/**
 * Shared light/dark theming for AG Charts.
 *
 * Charts are the one part of the UI the CSS ramp cannot reach: AG Charts paints
 * to a canvas, reading its colours once at construction. So they need to be
 * told the theme explicitly, and rebuilt when it changes.
 *
 * AG Charts already ships a dark theme (`ag-default-dark`) that handles axes,
 * gridlines, labels, legend and tooltips. Rather than re-specify all of that we
 * select its theme and override only the canvas background, so the chart sits
 * flush on our card surface instead of AG's near-black.
 *
 * Series colours are deliberately NOT changed: they are semantic (a channel
 * keeps one colour across the donut, the line chart and the grid badges), and
 * the palette is saturated enough to read on both backgrounds.
 */

const isDark = () => document.documentElement.classList.contains("theme-dark");

// The palette lives in CSS custom properties, which is also what keeps charts
// in step if the surface colours are ever retuned.
export const surfaceFill = () => {
  const triplet = getComputedStyle(document.documentElement)
    .getPropertyValue("--c-surface")
    .trim();

  // Before the stylesheet has applied, fall back to something sane rather than
  // emitting `rgb()` and having AG Charts throw.
  if (!triplet) return isDark() ? "#1a1c21" : "#ffffff";
  return `rgb(${triplet})`;
};

/**
 * Text colour for labels a chart draws itself (donut inner labels), which sit
 * outside whatever the base theme styles.
 *
 *   "strong" — the headline value
 *   "muted"  — its caption
 */
export function labelColour(weight) {
  const triplet = getComputedStyle(document.documentElement)
    .getPropertyValue(weight === "muted" ? "--c-gray-500" : "--c-gray-900")
    .trim();

  if (!triplet) return weight === "muted" ? "#6B7280" : "#111827";
  return `rgb(${triplet})`;
}

/**
 * Chart options fragment to spread into an `AgCharts.create({...})` call.
 *
 *   AgCharts.create({ container, data, series, ...chartTheme() })
 */
export function chartTheme() {
  return {
    theme: isDark() ? "ag-default-dark" : "ag-default",
    background: { fill: surfaceFill() },
  };
}

/**
 * Rebuild `hook.<method>()` whenever the theme flips.
 *
 * Call from `mounted()`; `stopThemeWatch` in `destroyed()`. The event comes
 * from ThemeHook, which fires it after the class on <html> has already changed,
 * so reading the theme inside the callback is safe.
 */
export function watchTheme(hook, rebuild) {
  hook.__onThemeChange = () => rebuild.call(hook);
  window.addEventListener("tms:theme-changed", hook.__onThemeChange);
}

export function stopThemeWatch(hook) {
  if (hook.__onThemeChange) {
    window.removeEventListener("tms:theme-changed", hook.__onThemeChange);
    hook.__onThemeChange = null;
  }
}
